import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { syncAll, countPending } from "../lib/sync";
import { useAuth } from "./AuthContext";

const SyncContext = createContext(null);
export const useSync = () => useContext(SyncContext);

export function SyncProvider({ children }) {
  const { user } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState(0);
  const [lastSync, setLastSync] = useState(null);

  const refreshPending = useCallback(async () => {
    setPending(await countPending());
  }, []);

  const runSync = useCallback(async () => {
    if (!navigator.onLine || !user) return;
    setSyncing(true);
    try {
      await syncAll();
      setLastSync(new Date());
    } finally {
      setSyncing(false);
      await refreshPending();
    }
  }, [user, refreshPending]);

  useEffect(() => {
    const on = () => {
      setOnline(true);
      runSync();
    };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [runSync]);

  useEffect(() => {
    if (user) {
      runSync();
      refreshPending();
      const iv = setInterval(runSync, 30000);
      return () => clearInterval(iv);
    }
  }, [user, runSync, refreshPending]);

  return (
    <SyncContext.Provider
      value={{ online, syncing, pending, lastSync, runSync, refreshPending }}
    >
      {children}
    </SyncContext.Provider>
  );
}
